/**
 * litSet -- BFS over the dependency graph from a selected
 * capability cell, computing the downstream blast set.
 *
 * Used by the page shell to dim non-blast cells when a cell is
 * selected (the on-select brass blast path). Pure helper;
 * symmetric to the constellation's `reachableFrom`.
 */
import type { CapabilityDependencyEdge } from "shared/api/types";

/** Build map: upstream_id -> [dependent_id...] -- "who depends on
 * me". Selecting a node lights up everything DOWNSTREAM (the
 * dependents that would break if this fails). */
export function buildDependentMap(
  edges: CapabilityDependencyEdge[],
): Map<string, string[]> {
  const out = new Map<string, string[]>();
  for (const e of edges) {
    if (!out.has(e.upstream_id)) out.set(e.upstream_id, []);
    out.get(e.upstream_id)!.push(e.dependent_id);
  }
  return out;
}

/** BFS reachable downstream from seed; INCLUDES the seed in the
 * returned set so the page shell can compute "is this cell in
 * the blast?" with a single Set.has() check.
 *
 * Cycle-safe: walks via a Set; bounded by the node count. */
export function reachableDownstream(
  seedId: string,
  edges: CapabilityDependencyEdge[],
): Set<string> {
  const dependents = buildDependentMap(edges);
  const out = new Set<string>([seedId]);
  const stack: string[] = [seedId];
  while (stack.length > 0) {
    const cur = stack.pop()!;
    for (const next of dependents.get(cur) ?? []) {
      if (out.has(next)) continue;
      out.add(next);
      stack.push(next);
    }
  }
  return out;
}
